import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { SupportRequestModalData } from '@app/support-center/model/support-center-model';
import { SupportCenterService } from '@app/support-center/services/support-center.service';
import {
  FilePicker,
  PickFilesOptions,
} from '@capawesome/capacitor-file-picker';
import {
  PopoverController,
  ActionSheetController,
  ViewWillEnter,
} from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { PlatformEnum } from '@panamax/app-state';
import { EllipsisOptionsPopoverComponent } from '@shared/components/popovers/ellipsis-options/ellipsis-options-popover.component';
import { b64toBlob } from '@shared/helpers/file.helpers';
import { Button } from '@shared/models/ellipsis-option.model';
import {
  FILE_EXTENSIONS_NO_PHOTOS,
  FILE_OPTIONS,
  PHOTO_OPTIONS,
  UserFile,
} from '@shared/services/file/model/file.model';
import { UserPhoto } from '@shared/services/photo/model/photo.model';
import { IssueDetails, IssueOptions, SupportRequest } from '@usf/list-types';
import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'app-support-request-modal',
  templateUrl: './support-request-modal.component.html',
  styleUrls: ['./support-request-modal.component.scss'],
})
export class SupportRequestModalComponent implements OnInit {
  @Input() platformType: PlatformEnum;
  @Input() email: string;
  @Input() issueOptions: IssueOptions[];
  @Input() photoArray: UserPhoto[];
  @Output() dismissEvent: EventEmitter<void> = new EventEmitter();
  @Output() photoAddEvent: EventEmitter<void> = new EventEmitter();
  @Output() photoDeleteEvent: EventEmitter<UserPhoto> = new EventEmitter();
  @Output() submitEvent: EventEmitter<SupportRequestModalData> =
    new EventEmitter();

  platformEnum = PlatformEnum;
  showEmailTooltip = true;
  selectedReason: IssueDetails;
  comments: string;
  photos: any;
  acceptedPhotoTypes = PHOTO_OPTIONS;
  acceptedFileTypes = FILE_OPTIONS;
  file: File;
  isFileValid = false;
  displayPageError = new BehaviorSubject(false);
  reasonButtons: Button[];
  supportRequestData: SupportRequest;

  constructor(
    private popoverController: PopoverController,
    private actionSheetController: ActionSheetController,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    if (!!this.email) {
      this.showEmailTooltip = false;
    }
    this.reasonButtons = this.createReasonButtons(this.issueOptions);
  }

  async openSelectFormatDropdown(event: any) {
    if (this.platformType === this.platformEnum.desktop) {
      this.openPopover(event, this.reasonButtons);
    } else {
      this.openActionSheet(this.reasonButtons);
    }
  }

  async openPopover(event: any, buttons: any[]) {
    const popover = await this.popoverController.create({
      component: EllipsisOptionsPopoverComponent,
      event,
      componentProps: {
        buttons,
      },
      cssClass: 'ellipsis-options-popover-desktop',
      mode: 'ios',
      showBackdrop: false,
    });
    await popover.present();
  }

  async openActionSheet(buttons: any[]) {
    buttons.push({
      text: this.translateService.instant('i18n.common.cancel'),
      role: 'cancel',
      cssClass: 'cancel-button',
    });
    const actionSheet = await this.actionSheetController.create({
      cssClass: 'action-sheet-touch',
      mode: 'ios',
      buttons,
    });
    await actionSheet.present();
  }

  dismissBottomHalfPopups() {
    if (this.platformType === this.platformEnum.desktop) {
      this.popoverController.dismiss();
    } else {
      this.actionSheetController.dismiss();
    }
  }

  shouldClosePopoverWithDelay(event: any) {
    this.closePopoverOnBlur();
    event?.currentTarget?.shadowRoot?.firstElementChild.addEventListener(
      'mouseenter',
      async () => {
        if (this.popoverController.getTop()) {
          await setTimeout(async () => {
            if (await this.popoverController.getTop()) {
              this.popoverController.dismiss();
            }
          }, 5000);
        }
      },
    );
  }

  closePopover = async () => {
    if (this.popoverController.getTop()) {
      await this.popoverController.dismiss();
    }
  };

  closePopoverOnBlur() {
    window.addEventListener('blur', this.closePopover, { once: true });
  }

  removeWindowListener = () => {
    window.removeEventListener('blur', this.closePopover);
  };

  dismiss() {
    this.dismissEvent.emit();
  }

  handlePhotoAddRequest = () => {
    this.photoAddEvent.emit();
  };

  handlePhotoDeleteRequest = (event: UserPhoto) => {
    this.photoDeleteEvent.emit(event);
  };

  handleFileAddRequest = async () => {
    try {
      const selectedFiles = await FilePicker.pickFiles({
        types: FILE_EXTENSIONS_NO_PHOTOS,
        limit: 1,
        multiple: false,
        readData: true,
      } as PickFilesOptions);
      let file: File;

      if (selectedFiles.files.length > 0) {
        selectedFiles.files[0].blob = b64toBlob(selectedFiles.files[0].data);
        file = new File(
          [selectedFiles.files[0].blob],
          selectedFiles.files[0].name,
          { type: selectedFiles.files[0].mimeType },
        );
        this.processFile(file);
      }
    } catch (ignore) {}
  };

  handleFileDeleteRequest() {
    this.file = undefined;
    this.isFileValid = false;
    this.displayPageError.next(false);
  }

  processFile = (file: File) => {
    this.displayPageError.next(false);
    if (this.validateFileExtension(file.type)) {
      this.isFileValid = true;
    } else {
      this.isFileValid = false;
      this.displayPageError.next(true);
    }

    this.file = this.isFileValid ? file : undefined;
  };

  validateFileExtension = (fileExtension: string) => {
    const acceptedFormats = FILE_EXTENSIONS_NO_PHOTOS;
    return acceptedFormats.includes(fileExtension);
  };

  createReasonButtons = (issues: IssueOptions[]): Button[] => {
    const reasonButtons: Button[] = [];
    issues.forEach(option => {
      option.issueDetails.forEach(issue => {
        const button = {
          text: issue.displayText,
          handler: () => {
            this.selectedReason = issue;
            this.dismissBottomHalfPopups();
          },
        };
        reasonButtons.push(button);
      });
    });
    return reasonButtons;
  };

  submitSupportRequest = () => {
    const supportRequestData: SupportRequestModalData = {
      file: this.file,
      requestReason: this.selectedReason,
      email: this.email,
      comments: this.comments,
    };
    this.submitEvent.emit(supportRequestData);
    this.dismissEvent.emit();
  };
}
